import {
    SITE_URL,
    SITE_NAME,
    SITE_DEFAULT_TITLE,
    SITE_DEFAULT_DESCRIPTION,
    OG_IMAGE_PATH,
    SOCIAL_SAME_AS,
    GEO,
} from "@/lib/site";
import {
    CONTACT,
    PRESTATIONS,
    FAQ_HOME,
    FAQ_EXTRA,
    GOOGLE_RATING,
    REVIEWS,
    TARIFS,
    GUIDES,
    ZONES,
} from "@/lib/data";

export function abs(path = "/") {
    if (!path) return SITE_URL;
    if (path.startsWith("http")) return path;
    return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export function localBusinessJsonLd() {
    return {
        "@context": "https://schema.org",
        "@type": ["LocalBusiness", "HealthAndBeautyBusiness"],
        "@id": `${SITE_URL}/#business`,
        name: CONTACT.name,
        alternateName: SITE_NAME,
        description: SITE_DEFAULT_DESCRIPTION,
        url: SITE_URL,
        image: abs(OG_IMAGE_PATH),
        telephone: CONTACT.phoneRaw,
        email: CONTACT.email,
        priceRange: "€€",
        currenciesAccepted: "EUR",
        paymentAccepted: "Cash, Check, Bank Transfer",
        address: {
            "@type": "PostalAddress",
            streetAddress: CONTACT.address.street,
            addressLocality: CONTACT.address.city,
            postalCode: CONTACT.address.zip,
            addressRegion: CONTACT.address.region,
            addressCountry: "FR",
        },
        geo: {
            "@type": "GeoCoordinates",
            latitude: GEO.latitude,
            longitude: GEO.longitude,
        },
        openingHoursSpecification: [
            {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: "Monday",
                opens: "14:00",
                closes: "18:00",
            },
            {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: "Tuesday",
                opens: "09:00",
                closes: "12:00",
            },
            {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: "Tuesday",
                opens: "13:30",
                closes: "18:00",
            },
            {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: "Wednesday",
                opens: "09:00",
                closes: "12:00",
            },
            {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: "Thursday",
                opens: "09:00",
                closes: "12:00",
            },
            {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: "Thursday",
                opens: "13:30",
                closes: "18:00",
            },
            {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: "Friday",
                opens: "09:00",
                closes: "18:00",
            },
            {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: "Saturday",
                opens: "09:00",
                closes: "18:00",
                description: "Sur rendez-vous",
            },
        ],
        areaServed: [
            { "@type": "City", name: "Challes-les-Eaux" },
            { "@type": "City", name: "Aix-les-Bains" },
            { "@type": "City", name: "Chambéry" },
            { "@type": "AdministrativeArea", name: "Savoie" },
        ],
        sameAs: SOCIAL_SAME_AS,
        aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: String(GOOGLE_RATING.rating),
            bestRating: "5",
            worstRating: "1",
            ratingCount: String(GOOGLE_RATING.count),
        },
        review: REVIEWS.map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.author },
            reviewRating: {
                "@type": "Rating",
                ratingValue: String(r.rating),
                bestRating: "5",
            },
            reviewBody: r.quote,
        })),
        hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Prestations de réflexologie",
            itemListElement: PRESTATIONS.map((p) => ({
                "@type": "Offer",
                itemOffered: {
                    "@type": "Service",
                    name: p.name,
                    url: abs(`/prestations/${p.slug}`),
                },
                price: String(parseFloat(p.price)),
                priceCurrency: "EUR",
            })),
        },
    };
}

export function personJsonLd() {
    return {
        "@context": "https://schema.org",
        "@type": "Person",
        "@id": `${SITE_URL}/#person`,
        name: CONTACT.name,
        jobTitle: CONTACT.role,
        url: abs("/a-propos"),
        worksFor: { "@id": `${SITE_URL}/#business` },
        address: {
            "@type": "PostalAddress",
            addressLocality: CONTACT.address.city,
            addressRegion: CONTACT.address.region,
            addressCountry: "FR",
        },
        telephone: CONTACT.phoneRaw,
        email: CONTACT.email,
        image: abs("/images/joelle-portrait.webp"),
        sameAs: SOCIAL_SAME_AS,
    };
}

export function websiteJsonLd() {
    return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        name: SITE_NAME,
        url: SITE_URL,
        inLanguage: "fr-FR",
        publisher: { "@id": `${SITE_URL}/#business` },
        description: SITE_DEFAULT_DESCRIPTION,
    };
}

export function faqJsonLd(items) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: {
                "@type": "Answer",
                text: f.a,
            },
        })),
    };
}

export function breadcrumbJsonLd(items) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: item.label,
            ...(item.to ? { item: abs(item.to) } : {}),
        })),
    };
}

export function serviceJsonLd(prestation) {
    const price = parseFloat(String(prestation.price).replace(/[^\d.]/g, ""));
    return {
        "@context": "https://schema.org",
        "@type": "Service",
        name: prestation.name,
        description: prestation.principle,
        url: abs(`/prestations/${prestation.slug}`),
        provider: { "@id": `${SITE_URL}/#business` },
        areaServed: {
            "@type": "City",
            name: CONTACT.address.city,
        },
        offers: {
            "@type": "Offer",
            priceCurrency: "EUR",
            price: String(price),
            availability: "https://schema.org/InStock",
            url: abs("/contact"),
        },
    };
}

export function articleJsonLd(guide) {
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: guide.title,
        description: guide.description,
        datePublished: guide.datePublished,
        dateModified: guide.dateModified || guide.datePublished,
        author: {
            "@type": "Person",
            name: CONTACT.name,
            url: abs("/a-propos"),
        },
        publisher: {
            "@type": "Organization",
            name: CONTACT.name,
            logo: {
                "@type": "ImageObject",
                url: abs("/images/logo-joelle-black.webp"),
            },
        },
        mainEntityOfPage: abs(`/guides/${guide.slug}`),
        image: abs(guide.image || OG_IMAGE_PATH),
        inLanguage: "fr-FR",
    };
}

/** SpeakableSpecification — blocs AnswerBlock citation-friendly. */
export function speakableJsonLd(path, cssSelectors = ['[data-speakable="true"]']) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${abs(path)}#webpage`,
        url: abs(path),
        speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: cssSelectors,
        },
        isPartOf: { "@id": `${SITE_URL}/#website` },
    };
}

/** HowTo — parcours première séance (page conversion). */
export function howToFirstSessionJsonLd() {
    return {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "Comment se déroule une première séance de réflexologie",
        description:
            "Étapes d'une première séance chez Joëlle Berthaud à Challes-les-Eaux : échange, protocole, soin, conseils.",
        totalTime: "PT1H",
        estimatedCost: {
            "@type": "MonetaryAmount",
            currency: "EUR",
            value: "60",
        },
        step: SESSION_STEPS_FOR_HOWTO(),
    };
}

function SESSION_STEPS_FOR_HOWTO() {
    // Import dynamique évité : miroir de SESSION_STEPS (data.js)
    return [
        {
            "@type": "HowToStep",
            position: 1,
            name: "Échange",
            text: "Temps d'écoute sur vos attentes, votre état du jour et vos antécédents utiles.",
        },
        {
            "@type": "HowToStep",
            position: 2,
            name: "Choix du protocole",
            text: "Plantaire, palmaire, Dien Chan, Chan'Beauté ou combinaison — adapté à vos besoins.",
        },
        {
            "@type": "HowToStep",
            position: 3,
            name: "Le soin",
            text: "45 à 60 minutes de travail précis et enveloppant, à votre rythme.",
        },
        {
            "@type": "HowToStep",
            position: 4,
            name: "Retour et conseils",
            text: "Observations, gestes d'auto-soin éventuels, et discussion d'un suivi si besoin.",
        },
    ];
}

export function allFaqItems() {
    return [...FAQ_HOME, ...FAQ_EXTRA];
}

/** Titre HTML final (évite le double « | Joëlle Berthaud »). */
export function formatPageTitle(title) {
    if (!title) return SITE_DEFAULT_TITLE;
    if (title.includes("Joëlle")) return title;
    return `${title} | Joëlle Berthaud`;
}

/** Meta + JSON-LD configs used by pages and the postbuild prerender script. */
export function getPageSeo(pathname) {
    const path = pathname.replace(/\/$/, "") || "/";

    if (path === "/") {
        return {
            path: "/",
            title: "Réflexologue à Challes-les-Eaux (Savoie) | Joëlle Berthaud",
            description: SITE_DEFAULT_DESCRIPTION,
            h1: "Réflexologue à Challes-les-Eaux — écouter le corps, retrouver son équilibre",
            answer:
                "Joëlle Berthaud est réflexologue certifiée à Challes-les-Eaux (Savoie). Elle propose la réflexologie plantaire, palmaire, faciale Dien Chan et Chan'Beauté, sur rendez-vous au cabinet, 1981 Route Royale. Contact : 06 63 34 03 97.",
            jsonLd: [
                localBusinessJsonLd(),
                websiteJsonLd(),
                personJsonLd(),
                faqJsonLd(FAQ_HOME),
                speakableJsonLd("/"),
            ],
        };
    }

    if (path === "/a-propos") {
        return {
            path: "/a-propos",
            title: "À propos — Joëlle Berthaud, réflexologue en Savoie",
            description:
                "Parcours, formations FFPER, Dien Chan et Chan'Beauté. Joëlle Berthaud, réflexologue à Challes-les-Eaux : une approche douce, à l'écoute du rythme de chacun.",
            h1: "Joëlle Berthaud, réflexologue à Challes-les-Eaux",
            answer:
                "Joëlle Berthaud est réflexologue certifiée (FFPER, méthode Eunice Ingham), formée également au Dien Chan et au Chan'Beauté. Elle reçoit au cabinet à Challes-les-Eaux depuis sa rencontre avec la réflexologie en 2018.",
            breadcrumbs: [
                { label: "Accueil", to: "/" },
                { label: "À propos" },
            ],
            jsonLd: [
                personJsonLd(),
                breadcrumbJsonLd([
                    { label: "Accueil", to: "/" },
                    { label: "À propos", to: "/a-propos" },
                ]),
            ],
        };
    }

    if (path === "/prestations") {
        return {
            path: "/prestations",
            title: "Prestations de réflexologie à Challes-les-Eaux",
            description:
                "Réflexologie plantaire, palmaire, faciale Dien Chan et Chan'Beauté à Challes-les-Eaux. Découvrez les quatre pratiques proposées par Joëlle Berthaud.",
            h1: "Prestations de réflexologie à Challes-les-Eaux",
            answer:
                "Joëlle Berthaud propose quatre pratiques : réflexologie plantaire (60 min), palmaire (45 min), faciale Dien Chan (45 min) et Chan'Beauté (60 min), au cabinet de Challes-les-Eaux en Savoie.",
            breadcrumbs: [
                { label: "Accueil", to: "/" },
                { label: "Prestations" },
            ],
            jsonLd: [
                breadcrumbJsonLd([
                    { label: "Accueil", to: "/" },
                    { label: "Prestations", to: "/prestations" },
                ]),
            ],
        };
    }

    const prestaMatch = path.match(/^\/prestations\/([^/]+)$/);
    if (prestaMatch) {
        const p = PRESTATIONS.find((x) => x.slug === prestaMatch[1]);
        if (p) {
            return {
                path: `/prestations/${p.slug}`,
                title: `${p.name} à Challes-les-Eaux`,
                description: `${p.name} (${p.duration}, ${p.price}) avec Joëlle Berthaud à Challes-les-Eaux. ${p.short} ${p.differentiator}`,
                h1: `${p.name} à Challes-les-Eaux`,
                answer: `${p.name} : séance de ${p.duration} à ${p.price} chez Joëlle Berthaud, réflexologue à Challes-les-Eaux (Savoie). ${p.principle.slice(0, 180)}…`,
                breadcrumbs: [
                    { label: "Accueil", to: "/" },
                    { label: "Prestations", to: "/prestations" },
                    { label: p.name },
                ],
                jsonLd: [
                    serviceJsonLd(p),
                    breadcrumbJsonLd([
                        { label: "Accueil", to: "/" },
                        { label: "Prestations", to: "/prestations" },
                        {
                            label: p.name,
                            to: `/prestations/${p.slug}`,
                        },
                    ]),
                    faqJsonLd([
                        {
                            q: `Comment se déroule une séance de ${p.name.toLowerCase()} ?`,
                            a: `La séance dure ${p.duration}. Après un temps d'échange, le protocole est adapté à votre état du jour. Tarif indicatif : ${p.price}.`,
                        },
                        {
                            q: `À qui s'adresse ${p.name.toLowerCase()} ?`,
                            a: p.forWhom,
                        },
                    ]),
                ],
            };
        }
    }

    if (path === "/tarifs") {
        const list = TARIFS.map(
            (t) => `${t.name} (${t.duration}) : ${t.price} €`,
        ).join(". ");
        return {
            path: "/tarifs",
            title: "Tarifs réflexologie à Challes-les-Eaux",
            description: `Tarifs indicatifs des séances de réflexologie chez Joëlle Berthaud à Challes-les-Eaux. ${list}`,
            h1: "Tarifs de réflexologie à Challes-les-Eaux",
            answer: `Les tarifs indicatifs chez Joëlle Berthaud à Challes-les-Eaux vont de 35 € (enfants) à 75 € (séance combinée). La réflexologie plantaire est à 60 € (60 min). Paiement espèces, chèque ou virement.`,
            breadcrumbs: [
                { label: "Accueil", to: "/" },
                { label: "Tarifs" },
            ],
            jsonLd: [
                breadcrumbJsonLd([
                    { label: "Accueil", to: "/" },
                    { label: "Tarifs", to: "/tarifs" },
                ]),
                speakableJsonLd("/tarifs"),
            ],
        };
    }

    if (path === "/entreprises") {
        return {
            path: "/entreprises",
            title: "Réflexologie en entreprise en Savoie",
            description:
                "Interventions de réflexologie en entreprise, ateliers et conférences en Savoie. Prévention du stress et bien-être au travail avec Joëlle Berthaud.",
            h1: "Réflexologie en entreprise en Savoie",
            answer:
                "Joëlle Berthaud intervient en entreprise en Savoie (Challes-les-Eaux, Aix-les-Bains, Chambéry et alentours) : séances express, ateliers et conférences autour du stress et de l'écoute du corps. Devis sur demande.",
            breadcrumbs: [
                { label: "Accueil", to: "/" },
                { label: "Entreprises" },
            ],
            jsonLd: [
                breadcrumbJsonLd([
                    { label: "Accueil", to: "/" },
                    { label: "Entreprises", to: "/entreprises" },
                ]),
            ],
        };
    }

    if (path === "/contact") {
        return {
            path: "/contact",
            title: "Prendre rendez-vous — réflexologue Challes-les-Eaux",
            description:
                "Contactez Joëlle Berthaud pour un rendez-vous de réflexologie à Challes-les-Eaux. Téléphone 06 63 34 03 97, email, formulaire et plan d'accès.",
            h1: "Prendre rendez-vous à Challes-les-Eaux",
            answer:
                "Pour prendre rendez-vous avec Joëlle Berthaud, réflexologue à Challes-les-Eaux : WhatsApp ou téléphone 06 63 34 03 97, email joelleberthaud@reflexologue-savoie.com, ou formulaire. Cabinet : 1981 Route Royale, 73190 Challes-les-Eaux.",
            breadcrumbs: [
                { label: "Accueil", to: "/" },
                { label: "Contact" },
            ],
            jsonLd: [
                localBusinessJsonLd(),
                breadcrumbJsonLd([
                    { label: "Accueil", to: "/" },
                    { label: "Contact", to: "/contact" },
                ]),
                speakableJsonLd("/contact"),
            ],
        };
    }

    if (path === "/faq") {
        const items = allFaqItems();
        return {
            path: "/faq",
            title: "FAQ réflexologie — questions fréquentes",
            description:
                "Réponses aux questions fréquentes sur la réflexologie : déroulement d'une séance, tarifs, mutuelle, contre-indications, parking à Challes-les-Eaux.",
            h1: "FAQ — questions sur la réflexologie",
            answer:
                "La première séance dure environ 1 h. La réflexologie est une pratique complémentaire de bien-être : elle ne remplace pas un suivi médical. Joëlle Berthaud reçoit à Challes-les-Eaux, sur rendez-vous.",
            breadcrumbs: [
                { label: "Accueil", to: "/" },
                { label: "FAQ" },
            ],
            jsonLd: [
                faqJsonLd(items),
                breadcrumbJsonLd([
                    { label: "Accueil", to: "/" },
                    { label: "FAQ", to: "/faq" },
                ]),
                speakableJsonLd("/faq"),
            ],
        };
    }

    if (path === "/premiere-seance") {
        return {
            path: "/premiere-seance",
            title: "Première séance de réflexologie — à quoi s'attendre",
            description:
                "Comment se déroule une première séance de réflexologie chez Joëlle Berthaud à Challes-les-Eaux : échange, protocole, durée, conseils et suivi.",
            h1: "Votre première séance de réflexologie",
            answer:
                "Une première séance chez Joëlle Berthaud dure environ 1 heure : échange sur vos attentes, choix du protocole (plantaire, palmaire, Dien Chan…), soin de 45 à 60 minutes, puis conseils d'auto-soin. Cabinet à Challes-les-Eaux.",
            breadcrumbs: [
                { label: "Accueil", to: "/" },
                { label: "Première séance" },
            ],
            jsonLd: [
                breadcrumbJsonLd([
                    { label: "Accueil", to: "/" },
                    { label: "Première séance", to: "/premiere-seance" },
                ]),
                faqJsonLd([FAQ_HOME[0], FAQ_HOME[1], FAQ_HOME[2]]),
                howToFirstSessionJsonLd(),
                speakableJsonLd("/premiere-seance"),
            ],
        };
    }

    if (path === "/guides") {
        return {
            path: "/guides",
            title: "Guides réflexologie & bien-être — Joëlle Berthaud",
            description:
                "Articles et guides sur la réflexologie, le stress, le sommeil, le Dien Chan et le bien-être en entreprise, par Joëlle Berthaud (Savoie).",
            h1: "Guides et ressources sur la réflexologie",
            answer:
                "Retrouvez des guides pratiques rédigés par Joëlle Berthaud, réflexologue à Challes-les-Eaux : première séance, stress, sommeil, Dien Chan et interventions en entreprise.",
            breadcrumbs: [
                { label: "Accueil", to: "/" },
                { label: "Guides" },
            ],
            jsonLd: [
                breadcrumbJsonLd([
                    { label: "Accueil", to: "/" },
                    { label: "Guides", to: "/guides" },
                ]),
            ],
        };
    }

    const guideMatch = path.match(/^\/guides\/([^/]+)$/);
    if (guideMatch) {
        const g = GUIDES.find((x) => x.slug === guideMatch[1]);
        if (g) {
            return {
                path: `/guides/${g.slug}`,
                title: g.title,
                description: g.description,
                h1: g.title,
                answer: g.answer,
                breadcrumbs: [
                    { label: "Accueil", to: "/" },
                    { label: "Guides", to: "/guides" },
                    { label: g.shortTitle || g.title },
                ],
                jsonLd: [
                    articleJsonLd(g),
                    breadcrumbJsonLd([
                        { label: "Accueil", to: "/" },
                        { label: "Guides", to: "/guides" },
                        { label: g.title, to: `/guides/${g.slug}` },
                    ]),
                    ...(g.faq?.length ? [faqJsonLd(g.faq)] : []),
                    speakableJsonLd(`/guides/${g.slug}`),
                ],
            };
        }
    }

    if (path === "/proximite") {
        return {
            path: "/proximite",
            title: "Réflexologue près de chez vous — Savoie",
            description:
                "Réflexologue à Challes-les-Eaux, à proximité d'Aix-les-Bains et Chambéry. Joëlle Berthaud accueille les habitants de Savoie au cabinet.",
            h1: "Réflexologue en Savoie — Challes-les-Eaux et alentours",
            answer:
                "Le cabinet de Joëlle Berthaud se situe à Challes-les-Eaux, à quelques minutes d'Aix-les-Bains et de Chambéry. Réflexologie plantaire, palmaire, Dien Chan et Chan'Beauté sur rendez-vous.",
            breadcrumbs: [
                { label: "Accueil", to: "/" },
                { label: "À proximité" },
            ],
            jsonLd: [
                localBusinessJsonLd(),
                breadcrumbJsonLd([
                    { label: "Accueil", to: "/" },
                    { label: "À proximité", to: "/proximite" },
                ]),
            ],
        };
    }

    const zoneMatch = path.match(/^\/proximite\/([^/]+)$/);
    if (zoneMatch) {
        const z = ZONES.find((x) => x.slug === zoneMatch[1]);
        if (z) {
            return {
                path: `/proximite/${z.slug}`,
                title: z.title,
                description: z.description,
                h1: z.h1,
                answer: z.answer,
                breadcrumbs: [
                    { label: "Accueil", to: "/" },
                    { label: "À proximité", to: "/proximite" },
                    { label: z.name },
                ],
                jsonLd: [
                    localBusinessJsonLd(),
                    breadcrumbJsonLd([
                        { label: "Accueil", to: "/" },
                        { label: "À proximité", to: "/proximite" },
                        { label: z.name, to: `/proximite/${z.slug}` },
                    ]),
                ],
            };
        }
    }

    if (path === "/mentions-legales") {
        return {
            path: "/mentions-legales",
            title: "Mentions légales et confidentialité",
            description:
                "Mentions légales, hébergement, données personnelles et politique de confidentialité du site de Joëlle Berthaud, réflexologue.",
            h1: "Mentions légales et confidentialité",
            noindex: false,
            jsonLd: [
                breadcrumbJsonLd([
                    { label: "Accueil", to: "/" },
                    { label: "Mentions légales", to: "/mentions-legales" },
                ]),
            ],
        };
    }

    return {
        path,
        title: "Page introuvable",
        description: "La page demandée n'existe pas.",
        noindex: true,
        h1: "Page introuvable",
    };
}

/** All indexable paths for sitemap / prerender. */
export function getAllIndexablePaths() {
    return [
        "/",
        "/a-propos",
        "/prestations",
        ...PRESTATIONS.map((p) => `/prestations/${p.slug}`),
        "/tarifs",
        "/entreprises",
        "/contact",
        "/faq",
        "/premiere-seance",
        "/guides",
        ...GUIDES.map((g) => `/guides/${g.slug}`),
        "/proximite",
        ...ZONES.map((z) => `/proximite/${z.slug}`),
        "/mentions-legales",
    ];
}
